import { SyntheticEvent, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Divider,
  List,
  ListItem,
  ListItemText,
  Switch,
  useColorScheme,
} from "@mui/material";

import {
  getCurrentLocale,
  Locale,
  LOCALE_MAP_TRANSLATIONS,
} from "../../Locale";
import { setCookie } from "../../services/utils";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import ProfilePictureSection from "../UserPreferences/ProfilePictureSection";

type OptionType = {
  id: string;
  value: string;
};

const getOptionLabel = (option: OptionType) => option.value;

const isOptionEqualToValue = (option: OptionType, value: OptionType) =>
  option.id === value.id;

const UserPreferencesTab = () => {
  const intl = useIntl();
  const { mode, setMode } = useColorScheme();
  const [locale] = useState<Locale>(() => getCurrentLocale());

  const languagesOptions: OptionType[] = useMemo(
    () =>
      Object.entries(LOCALE_MAP_TRANSLATIONS).map(([key, value]) => ({
        id: key,
        value: intl.formatMessage({ id: value }),
      })),
    [intl]
  );

  const handleChangeLanguage = (_: SyntheticEvent, value: OptionType) => {
    setCookie("revbell-lang", value.id);
    window.location.reload();
  };

  return (
    <List sx={{ padding: 0 }}>
      <ListItem sx={{ py: 1, px: 0 }}>
        <ProfilePictureSection />
      </ListItem>
      <Divider sx={{ my: 0.5, borderColor: "background.default" }} />
      <ListItem sx={{ py: 1, px: 0 }}>
        <ListItemText primary={intl.formatMessage({ id: "DARK_THEME" })} />
        <Switch
          edge="end"
          onChange={() => setMode(mode === "dark" ? "light" : "dark")}
          checked={mode === "dark"}
        />
      </ListItem>
      <ListItem sx={{ py: 1, px: 0 }}>
        <ListItemText primary={intl.formatMessage({ id: "LANGUAGE" })} />
        <CustomAutocomplete
          disableClearable
          options={languagesOptions}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          textFieldSlotProps={{ inputLabel: { shrink: true } }}
          placeholder={intl.formatMessage({
            id: "PROFILE_SETTINGS_LANGUAGE_SELECTOR",
          })}
          onChange={handleChangeLanguage}
          defaultValue={languagesOptions.find((lang) => lang.id === locale)}
          sx={{
            width: "250px",
            "& .MuiFormControl-root": { margin: 0 },
          }}
        />
      </ListItem>
    </List>
  );
};

export default UserPreferencesTab;
